<template>
  <div class="threadContainer" style="height: 100%">
    <div id="m_threadcontentwrapper" style="height: 100%">
      <div v-if="loading" style="padding: 10px; text-align: center">
        <div class="ui active inline text loader"></div>
        Loading messages...
      </div>

      <div v-else id="m_threadcontent" ref="threadContent" class="messages-list">
        <div v-for="(msg, index) in messages" :key="index" class="bubble">
          <div v-if="msg.is_you" class="text-holder chat-me">
            <div class="text-wrapper chat-me">
              <div>
                <i class="issent isread read tiny green check circle icon"></i>
                <i
                  v-if="msg.is_new"
                  class="isread read tiny minus circle icon"
                ></i>
                <i v-else class="isread read tiny green check circle icon"></i>
                <span class="text-me">{{ msg.message }}</span>
              </div>
            </div>
            <img
              :src="profileImage(msg.user)"
              :alt="msg.user?.fullname"
              class="chat-profile-me"
            />
          </div>

          <div v-else class="text-holder chat-you">
            <div class="text-wrapper chat-you">
              <div>
                <span class="text-you">{{ msg.message }}</span>
              </div>
            </div>
            <img
              :src="profileImage(msg.user)"
              :alt="msg.user?.fullname"
              class="chat-profile-you"
            />
          </div>
        </div>
      </div>

      <div id="m_threadfooter" class="chat-footer">
        <div id="m_reply" class="chat-reply">
          <input
            type="text"
            v-model="replyText"
            @keyup.enter="sendReply"
            placeholder="Your reply"
            class="chat-reply-input"
            maxlength="255"
          />
          <button
            @click="sendReply"
            :disabled="!replyText.trim()"
            class="ui blue right floated icon button"
            style="margin-left: 0.5rem"
          >
            <i class="ui send icon"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick } from "vue";

const props = defineProps({
  messages: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["reply"]);
const replyText = ref("");
const threadContent = ref(null);

const profileImage = (user) => {
  // Use imagekit url globally defined somewhere or fallback
  const baseUrl = "https://ik.imagekit.io/weaudition";
  const imgStr = user?.profile_image || "default.jpg";
  // Check if we already have the full path (sometimes it happens)
  if (imgStr.includes("http")) return imgStr;
  return `${baseUrl}/tr:w-50,h-50,q-85/upload/userprofiles/${imgStr}`;
};

const scrollToBottom = (smooth = true) => {
  nextTick(() => {
    if (threadContent.value) {
      threadContent.value.scrollTo({
        top: threadContent.value.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  });
};

watch(
  () => props.messages,
  () => {
    scrollToBottom(true);
  },
  { deep: true }
);

watch(
  () => props.loading,
  (isLoading) => {
    if (!isLoading) {
      setTimeout(() => scrollToBottom(false), 50);
      setTimeout(() => scrollToBottom(false), 200);
    }
  }
);

onMounted(() => {
  scrollToBottom(false);
  setTimeout(() => scrollToBottom(false), 100);
});

const sendReply = () => {
  if (!replyText.value.trim()) return;
  emit("reply", replyText.value.trim());
  replyText.value = "";
  scrollToBottom(true);
};
</script>

<style scoped>
#m_threadfooter {
  background: #f8f8f8;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
}

#m_reply {
  margin: -0.5rem 0.3rem 0.5rem 0.5rem;
}

.bubble {
  margin-right: 10px;
  margin-left: 10px;
  position: relative;
  margin-top: 0.5rem;
}

.threadContainer {
  border-radius: 0.2857rem;
  border: 1px solid #ccc;
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
}

#m_threadcontentwrapper {
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  scrollbar-width: thin;
  background: #f8f8f8;
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  justify-content: space-between;
  border-top-right-radius: 3px;
  border-top-left-radius: 3px;
}

#m_threadcontent {
  margin-top: 1px;
  margin-right: 6px;
  display: flex;
  overflow-y: auto;
  padding-top: 1rem;
  padding-bottom: 1rem;
  flex-direction: column;
  padding-left: 13px;
  padding-right: 10px;
  scroll-behavior: smooth;
}

.row #m_threadcontent {
  max-height: 200px;
}

.row.videosetup #m_threadcontent {
  max-height: 320px;
}

.text-holder {
  display: flex;
  align-items: flex-end;
}

.text-holder.chat-me {
  justify-content: flex-end;
}

.text-wrapper {
  display: flex;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  margin-top: 0.5rem;
  flex-direction: column;
  order: 2;
  max-width: 20rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.text-wrapper.chat-me {
  align-items: flex-end;
  position: relative;
}

.text-wrapper.chat-me .isread.circle.icon {
  position: absolute;
  right: -4px;
  bottom: -11px;
  border-radius: 50%;
  margin: 0;
}

.text-wrapper.chat-me .isread.issent.circle.icon {
  right: 8px;
}

.text-wrapper.chat-you {
  align-items: flex-start;
  position: relative;
}

.text-wrapper.chat-you .isread.circle.icon {
  position: absolute;
  left: -4px;
}

.text-me {
  display: inline-block;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-left: 1rem;
  padding-right: 1rem;
  border-radius: 0.5rem;
  border-bottom-right-radius: 0;
  color: #ffffff;
  background-color: #60a5fa;
}

.chat-profile-me {
  order: 2;
  border-radius: 9999px;
  width: 2rem;
  height: 2rem;
}

.chat-profile-you {
  order: 1;
  border-radius: 9999px;
  width: 2rem;
  height: 2rem;
}

.text-you {
  display: inline-block;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-left: 1rem;
  padding-right: 1rem;
  border-radius: 0.5rem;
  border-bottom-left-radius: 0;
  color: #27272a;
  background-color: #e4e4e7;
}

.chat-footer {
  padding-top: 1rem;
  margin-bottom: 0.5rem;
  border-top-width: 2px;
  border-color: #e4e4e7;
}

@media (min-width: 640px) {
  .chat-footer {
    margin-bottom: 0;
  }
}

.chat-reply {
  display: flex;
  column-gap: 0.5rem;
}

.chat-reply-input {
  border-radius: 0.25rem;
  width: 100%;
  border-color: #e4e4e7;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-color: #fff;
  border-width: 1px;
  padding: 0.5rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5rem;
  box-shadow: 0 0 #0000;
}
</style>
